import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { OrganizationService } from "@/shared/services/organization.service";
import type { OrganizationPlanResponse } from "@/shared/types/common";
import type { CseInformations } from "@/shared/types/organization.types";

// ===== QUERY KEYS =====
export const ORGANIZATION_QUERY_KEYS = {
  all: ["organization"] as const,
  detail: (organizationId: string) => [...ORGANIZATION_QUERY_KEYS.all, organizationId] as const,
  plan: (organizationId: string) =>
    [...ORGANIZATION_QUERY_KEYS.all, "plan", organizationId] as const,
};

// ===== HOOKS =====

/**
 * Hook to fetch organization data by ID
 * @param organizationId - ID of the organization
 * @param options - Opt-in polling / focus refetch for callers that need fresher data
 * @returns TanStack Query result with organization data
 */
export const useOrganizationQuery = (
  organizationId: string | undefined,
  options?: OrganizationQueryOptions,
) => {
  return useQuery({
    queryKey: ORGANIZATION_QUERY_KEYS.detail(organizationId || ""),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }
      return await OrganizationService.getOrganization(organizationId);
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds - organization data doesn't change often
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
};

/**
 * Hook to get organization data from cache without refetching
 * Useful for components that need to access organization data but don't want to trigger a refetch
 * @param organizationId - ID of the organization
 * @returns Organization data from cache or undefined
 */
export const useOrganizationData = (organizationId: string | undefined) => {
  const { data } = useOrganizationQuery(organizationId);
  return data?.organization;
};

/**
 * Hook to fetch organization plan (GET /organizations/:id/plan), including SMS quota
 * Use this when you need fresh plan data (e.g. quota) instead of Redux-cached plan
 */
export const useOrganizationPlanQuery = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: ORGANIZATION_QUERY_KEYS.plan(organizationId || ""),
    queryFn: async (): Promise<OrganizationPlanResponse> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }
      return await OrganizationService.getOrganizationPlan(organizationId);
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000,
  });
};

// ===== TYPES =====
export type OrganizationQueryOptions = Partial<
  Pick<UseQueryOptions<CseInformations>, "refetchInterval" | "refetchOnWindowFocus">
>;
export type OrganizationQueryResult = ReturnType<typeof useOrganizationQuery>;
export type OrganizationData = CseInformations["organization"];
